import { Injectable, inject, signal } from '@angular/core';
import { ISetupPrerequisitos } from '@nomoh-monorepo/shared-types';
import { EstabelecimentoService } from '../feature-estabelecimento/estabelecimento.service';

export type SetupOperacionalOrigem = 'whatsapp' | 'agenda' | 'agenda-encaixe';

export interface SetupOperacionalValidacao {
  ok: boolean;
  pendencias: string[];
}

/**
 * Gate compartilhado: valida pré-requisitos via endpoint dedicado e controla o modal.
 */
@Injectable({ providedIn: 'root' })
export class SetupOperacionalGateService {
  private estabelecimentoService = inject(EstabelecimentoService);

  private _visible = signal(false);
  private _origem = signal<SetupOperacionalOrigem | null>(null);
  private _carregando = signal(false);
  private resolver: ((ok: boolean) => void) | null = null;

  $visible = this._visible.asReadonly();
  $origem = this._origem.asReadonly();
  $carregando = this._carregando.asReadonly();

  async estaCompleto(): Promise<boolean> {
    const resultado = await this.validar();
    return resultado.ok;
  }

  /**
   * Se completo, resolve true imediatamente.
   * Se incompleto, abre o modal e resolve quando o usuário concluir (true) ou cancelar (false).
   */
  async garantirPrerequisitos(origem: SetupOperacionalOrigem): Promise<boolean> {
    const resultado = await this.validar();
    if (resultado.ok) {
      return true;
    }

    return this.solicitarConclusao(origem);
  }

  /**
   * Abre o modal de setup e resolve quando o usuário conclui (true) ou cancela (false).
   * Não dispara nova validação — usar depois de `validar()` já ter falhado.
   */
  solicitarConclusao(origem: SetupOperacionalOrigem): Promise<boolean> {
    if (this._visible() && this.resolver) {
      return new Promise((resolve) => {
        const prev = this.resolver;
        this.resolver = (ok) => {
          prev?.(ok);
          resolve(ok);
        };
      });
    }

    this._origem.set(origem);
    this._visible.set(true);

    return new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  concluir(): void {
    this._visible.set(false);
    this._origem.set(null);
    const resolve = this.resolver;
    this.resolver = null;
    resolve?.(true);
  }

  cancelar(): void {
    this._visible.set(false);
    this._origem.set(null);
    const resolve = this.resolver;
    this.resolver = null;
    resolve?.(false);
  }

  async validar(): Promise<SetupOperacionalValidacao> {
    this._carregando.set(true);
    try {
      const flags = await this.estabelecimentoService.getSetupPrerequisitos();
      const pendencias = this.montarPendencias(flags);
      return { ok: flags.ok && pendencias.length === 0, pendencias };
    } catch (error) {
      console.error('Erro ao validar pré-requisitos de setup', error);
      return {
        ok: false,
        pendencias: [
          'Não foi possível verificar a configuração. Tente novamente.',
        ],
      };
    } finally {
      this._carregando.set(false);
    }
  }

  private montarPendencias(flags: ISetupPrerequisitos): string[] {
    const pendencias: string[] = [];

    if (!flags.temTiposServico) {
      pendencias.push('Nenhuma categoria de serviço cadastrada.');
    }

    if (!flags.temServicos) {
      pendencias.push('Nenhum serviço cadastrado.');
    }

    if (!flags.temProfissionais) {
      pendencias.push('Nenhum profissional cadastrado.');
      return pendencias;
    }

    if (!flags.temProfissionalDisponivel) {
      pendencias.push('Nenhum profissional disponível (todos de férias).');
    }

    if (!flags.temProfissionalComAtendimento) {
      pendencias.push(
        'Nenhum profissional com serviço vinculado (presta atendimento).'
      );
    }

    return pendencias;
  }
}
